// Opening move: StressLineAnalysisPlugin - Batch line-level stress analysis
// Epic 05 Scope 02: Throttled listener that batches visible lyrics via analyzeBatch
// Big play: Maps backend response offsets to LyricsTextNode keys for metadata dispatch

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getRoot,
  $isElementNode,
  type LexicalNode,
} from 'lexical'
import type { StressCoordinatorService } from '../services/StressCoordinatorService'
import {
  $isLyricsTextNode,
  type LyricsTextNode,
} from '../nodes/LyricsTextNode'
import {
  STRESS_LINE_ANALYSIS_TAG,
  STRESS_CONTEXTUAL_TAG,
  STRESS_AUTOFILL_TAG,
  STRESS_BACKEND_ERROR_TAG,
  SET_LINE_STRESS_METADATA_COMMAND,
  type SetLineStressMetadataPayload,
} from './StressDataPlugin'

// ============================================================================
// Configuration
// ============================================================================

/**
 * Throttle delay for batch line analysis
 *
 * Main play: Wait ≥1000ms after typing stops before batching lines
 * Epic 05 requirement: ≥1000ms throttle to avoid spamming backend
 */
const THROTTLE_DELAY_MS = 1000

// ============================================================================
// Plugin Component
// ============================================================================

export interface StressLineAnalysisPluginProps {
  /**
   * StressCoordinatorService instance for batch analysis
   *
   * Main play: DI-friendly design - inject mock for tests, real for production
   */
  coordinator: StressCoordinatorService
}

/**
 * StressLineAnalysisPlugin - Batch line-level stress analysis
 *
 * Epic 05 Scope 02: Throttled listener that batches visible lyrics for backend analysis
 * Main play: Registers update listener that collects visible LyricsTextNodes, batches content
 * Big play: Maps backend word offsets back to node keys and dispatches line metadata
 *
 * Critical requirements from iteration plan:
 * - MUST register ≥1000ms throttled update listener
 * - MUST batch visible lyrics via analyzeBatch endpoint
 * - MUST map response offsets to LyricsTextNode keys
 * - MUST dispatch SET_LINE_STRESS_METADATA_COMMAND with STRESS_LINE_ANALYSIS_TAG
 * - MUST skip cycles tagged with STRESS_LINE_ANALYSIS_TAG to prevent loops
 * - MUST log errors with [LEXICAL_STRESS_ERROR] prefix
 *
 * Reference: .local_docs/lexical/core-docs/03-commands.md - Update listener patterns
 */
export default function StressLineAnalysisPlugin({
  coordinator,
}: StressLineAnalysisPluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Opening move: Track throttle timer and in-flight analysis
    let throttleTimer: ReturnType<typeof setTimeout> | null = null
    let isAnalyzing = false

    /**
     * Recursively collect all LyricsTextNodes from editor tree
     *
     * Time to tackle the tricky bit: Walk tree using public API
     * Reference: .local_docs/lexical/core-docs/01-nodes.md - Tree traversal patterns
     */
    const collectLyricsTextNodes = (node: LexicalNode): LyricsTextNode[] => {
      const textNodes: LyricsTextNode[] = []

      if ($isLyricsTextNode(node)) {
        textNodes.push(node)
      }

      // Recursively collect from children (only ElementNodes have children)
      if ($isElementNode(node)) {
        const children = node.getChildren()
        for (const child of children) {
          textNodes.push(...collectLyricsTextNodes(child))
        }
      }

      return textNodes
    }

    /**
     * Perform batch line analysis and dispatch metadata
     *
     * Main play: Collect lines from visible nodes, call analyzeBatch, map back to keys
     * Big play: Dispatches SET_LINE_STRESS_METADATA_COMMAND with per-node metadata
     */
    const performBatchAnalysis = async () => {
      if (isAnalyzing) {
        return // Guard: Skip if already analyzing
      }

      isAnalyzing = true

      try {
        // Opening move: Collect all visible lyrics text nodes
        const nodeData: Array<{ key: string; text: string }> = []

        editor.getEditorState().read(() => {
          const root = $getRoot()
          const textNodes = collectLyricsTextNodes(root)

          for (const node of textNodes) {
            const text = node.getTextContent().trim()
            if (text) {
              nodeData.push({ key: node.getKey(), text })
            }
          }
        })

        if (nodeData.length === 0) {
          return // No lines to analyze
        }

        // Main play: Extract just the text lines for batch API
        const lines = nodeData.map(nd => nd.text)

        // Big play: Call backend batch analysis
        const batchResult = await coordinator.analyzeBatch(lines, 'lyrical')

        // Time to tackle the tricky bit: Map backend results back to node keys
        // Victory lap: Build metadata payload for SET_LINE_STRESS_METADATA_COMMAND
        const metadata: SetLineStressMetadataPayload['metadata'] = {}

        for (let i = 0; i < batchResult.lines.length; i++) {
          const lineAnalysis = batchResult.lines[i]
          const nodeKey = nodeData[i]?.key

          if (nodeKey && lineAnalysis) {
            metadata[nodeKey] = {
              total_syllables: lineAnalysis.total_syllables,
              stressed_syllables: lineAnalysis.stressed_syllables,
              words: lineAnalysis.words.length,
              // Scope 09: Forward expected count for badge match quality
              expected_stressed_syllables: lineAnalysis.expected_stressed_syllables,
            }
          }
        }

        // Victory lap: Dispatch metadata command with STRESS_LINE_ANALYSIS_TAG
        if (Object.keys(metadata).length > 0) {
          editor.update(
            () => {
              editor.dispatchCommand(SET_LINE_STRESS_METADATA_COMMAND, { metadata })
            },
            { tag: STRESS_LINE_ANALYSIS_TAG }
          )
        }
      } catch (error) {
        // Error handling: Log with [LEXICAL_STRESS_ERROR] prefix
        const reason = error instanceof Error ? error.message : 'Unknown error'
        const errorMsg = `[LEXICAL_STRESS_ERROR] Batch line analysis failed: ${reason}`
        console.error(errorMsg)

        // Instrumentation hook for debugging (guards against SSR/Jest)
        if (typeof window !== 'undefined' && window.__LEXICAL_DEBUG__) {
          window.__LEXICAL_DEBUG__.record?.({
            type: 'stress-line-analysis-error',
            reason,
            timestamp: Date.now(),
          })
        }
      } finally {
        isAnalyzing = false
      }
    }

    /**
     * Schedule batch analysis with throttle
     *
     * Main play: Cancel existing timer and start new one
     * Big play: Ensures we wait ≥1000ms after last editor change before analyzing
     */
    const scheduleAnalysis = () => {
      if (throttleTimer !== null) {
        clearTimeout(throttleTimer)
      }
      throttleTimer = setTimeout(() => {
        throttleTimer = null
        performBatchAnalysis()
      }, THROTTLE_DELAY_MS)
    }

    // ========================================================================
    // Update Listener Registration
    // ========================================================================

    const removeUpdateListener = editor.registerUpdateListener(({ tags }) => {
      // CRITICAL: Guard against listener loops - skip our own updates
      if (tags.has(STRESS_LINE_ANALYSIS_TAG)) {
        return
      }

      // Main play: Also skip if update was from other stress plugins to avoid redundant work
      if (tags.has(STRESS_AUTOFILL_TAG) || tags.has(STRESS_CONTEXTUAL_TAG) || tags.has(STRESS_BACKEND_ERROR_TAG)) {
        return
      }

      // Big play: Schedule analysis for any content changes
      scheduleAnalysis()
    })

    // ========================================================================
    // Cleanup
    // ========================================================================

    return () => {
      removeUpdateListener()
      if (throttleTimer !== null) {
        clearTimeout(throttleTimer)
      }
    }
  }, [editor, coordinator])

  // Victory lap: Headless plugin returns null (no UI)
  return null
}

// Victory lap: StressLineAnalysisPlugin complete with throttled batch analysis
// Collects visible lyrics, calls backend, maps offsets to keys, dispatches metadata
// Ready for integration with baseline editor and comprehensive testing
